import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchCustomerSettings, getAuthToken, updateCustomerSettings } from "../api";
import {
  DEFAULT_CURRENCY,
  formatCurrencyAmount,
  getCurrencySymbol,
  normalizeCurrencyCode,
} from "../utils/currency";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_LANGUAGE,
  DEFAULT_REGION_FORMATTING_LOCALE,
  getIntlLocale,
  normalizeLanguage,
  normalizeRegionFormattingLocale,
} from "../utils/intlLocale";

const CustomerSettingsContext = createContext(null);

export const CustomerSettingsProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [settings, setSettings] = useState({
    currency: DEFAULT_CURRENCY,
    language: normalizeLanguage(localStorage.getItem("locale") || DEFAULT_LANGUAGE),
    regionFormattingLocale: DEFAULT_REGION_FORMATTING_LOCALE,
  });
  const [loading, setLoading] = useState(true);

  const normalizeSettings = useCallback((data = {}) => {
    const language = normalizeLanguage(data.language || localStorage.getItem("locale") || DEFAULT_LANGUAGE);
    return {
      currency: normalizeCurrencyCode(data.currency),
      language,
      regionFormattingLocale: normalizeRegionFormattingLocale(
        data.regionFormattingLocale || data.region_formatting_locale || getIntlLocale(language)
      ),
    };
  }, []);

  const reloadSettings = useCallback(async () => {
    try {
      getAuthToken();
      const data = await fetchCustomerSettings();
      const next = normalizeSettings(data);
      setSettings(next);
      localStorage.setItem("locale", next.language);
      if (i18n.language !== next.language) {
        i18n.changeLanguage(next.language);
      }
    } catch (error) {
      console.error("Failed to load customer settings:", error);
      setSettings(normalizeSettings());
    } finally {
      setLoading(false);
    }
  }, [i18n, normalizeSettings]);

  useEffect(() => {
    reloadSettings();
  }, [reloadSettings]);

  const saveSettings = useCallback(async (updates) => {
    const normalizedUpdates = {
      ...updates,
      ...(updates?.currency ? { currency: normalizeCurrencyCode(updates.currency) } : {}),
      ...(updates?.language ? { language: normalizeLanguage(updates.language) } : {}),
      ...(updates?.regionFormattingLocale ? { regionFormattingLocale: normalizeRegionFormattingLocale(updates.regionFormattingLocale) } : {}),
    };

    const previous = settings;
    setSettings((current) => ({ ...current, ...normalizedUpdates }));
    try {
      const saved = await updateCustomerSettings(normalizedUpdates);
      const next = normalizeSettings(saved);
      setSettings(next);
      localStorage.setItem("locale", next.language);
      if (i18n.language !== next.language) {
        i18n.changeLanguage(next.language);
      }
      return next;
    } catch (error) {
      setSettings(previous);
      throw error;
    }
  }, [i18n, normalizeSettings, settings]);

  const value = useMemo(() => ({
    settings,
    currency: normalizeCurrencyCode(settings.currency),
    language: normalizeLanguage(settings.language),
    regionFormattingLocale: normalizeRegionFormattingLocale(settings.regionFormattingLocale),
    loading,
    reloadSettings,
    saveSettings,
  }), [settings, loading, reloadSettings, saveSettings]);

  return (
    <CustomerSettingsContext.Provider value={value}>
      {children}
    </CustomerSettingsContext.Provider>
  );
};

export const useCustomerSettings = () => {
  const context = useContext(CustomerSettingsContext);
  if (!context) {
    throw new Error("useCustomerSettings must be used within CustomerSettingsProvider");
  }
  return context;
};

export const useCustomerCurrency = () => {
  const { currency, regionFormattingLocale } = useCustomerSettings();
  const locale = normalizeRegionFormattingLocale(regionFormattingLocale);

  return useMemo(() => ({
    currency,
    currencySymbol: getCurrencySymbol(locale, currency),
    formatCurrency: (value, options) => formatCurrencyAmount(locale, currency, value, options),
  }), [currency, locale]);
};

export const useCustomerLocale = () => {
  const { regionFormattingLocale } = useCustomerSettings();
  return normalizeRegionFormattingLocale(regionFormattingLocale);
};
